"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { IonContent, IonPage } from "@ionic/react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  IconButton,
} from "@mui/material"
import {
  TrendingUp,
  TrendingDown,
  SwapHoriz as MovementIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Timeline as TimelineIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import type { Movement } from "../../models/stocks"
import StockMovementList from "./stockmovementlist"
import { getStocksTransfer } from "../../api/stock-transfer/stock-transfer"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

const Movements: React.FC = () => {
  const [movements, setMovements] = useState<Movement[]>([])
  const [filteredMovements, setFilteredMovements] = useState<Movement[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [filter, setFilter] = useState<"all" | "in" | "out" | "transfer">("all")
  const [error, setError] = useState<string | null>(null)

  const fetchMovements = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await getStocksTransfer()
      const movementsData = response?.data?.data
        console.error("No data found in API response:", response?.data?.data)

      
      if (Array.isArray(movementsData)) {
        setMovements(movementsData)
      } else {
        console.error("Invalid data structure in API response:", response)
        setMovements([])
        setError("Received invalid data from the server.")
      }
    } catch (error) {
      console.error("Error fetching stock movements:", error)
      setMovements([])
      setError("An error occurred while fetching movements.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMovements()
  }, [])

  useEffect(() => {
    let filtered = movements.filter(
      (movement) =>
        movement.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movement.user?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movement.fromLocation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        movement.toLocation?.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    // // Apply filter
    // switch (filter) {
    //   case "in":
    //     filtered = filtered.filter((m) => m.type === "in" || m.type === "receive")
    //     break
    //   case "out":
    //     filtered = filtered.filter((m) => m.type === "out" || m.type === "issue")
    //     break
    //   case "transfer":
    //     filtered = filtered.filter((m) => m.type === "transfer")
    //     break
    //   default:
    //     break
    // }

    setFilteredMovements(movements)
  }, [searchTerm, movements, filter])

  const getTodayMovements = () => {
    const today = new Date().toDateString()
    return movements.filter((movement) => new Date(movement.date).toDateString() === today).length
  }

  const getTotalQuantityMoved = () => {
    return movements.reduce((total, movement) => total + (movement.quantity || 0), 0)
  }

  const getInboundMovements = () => {
    return movements.filter((m) => m.type === "in" || m.type === "receive").length
  }

  const getOutboundMovements = () => {
    return movements.filter((m) => m.type === "out" || m.type === "issue").length
  }

  const getTransferMovements = () => {
    return movements.filter((m) => m.type === "transfer").length
  }

  const getUniqueItems = () => {
    const uniqueItems = new Set(movements.map((m) => m.itemId))
    return uniqueItems.size
  }
console.log("Filtered Movements:", filteredMovements)
  return (
    <IonPage>
      <IonContent>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header */}
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    color="#1e293b"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <TimelineIcon />
                    Stock Movements
                  </Typography>
                  <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                    Track and monitor all stock movements and transactions
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchMovements}
                  disabled={loading}
                  sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                >
                  Refresh
                </Button>
              </Box>
            </HeaderCard>
          </StyledCard>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <IconButton size="small" onClick={() => setError(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          {/* Stats Dashboard */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <MovementIcon sx={{ fontSize: 32, mb: 1, color: "#3b82f6" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {movements?.length}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Total Movements
                </Typography>
              </StatsCard>
            </Grid> */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <TrendingUp sx={{ fontSize: 32, mb: 1, color: "#10b981" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getInboundMovements()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Inbound
                </Typography>
              </StatsCard>
            </Grid> */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <TrendingDown sx={{ fontSize: 32, mb: 1, color: "#ef4444" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getOutboundMovements()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Outbound
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatsCard>
                <InventoryIcon sx={{ fontSize: 32, mb: 1, color: "#f59e0b" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getTotalQuantityMoved().toLocaleString()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Items Moved
                </Typography>
              </StatsCard>
            </Grid> */}
          </Grid>

          {/* Additional Stats Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
              <StatsCard>
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                                    {movements?.length}

                </Typography>
                <Typography variant="body2" color="#64748b">
                  Total Movements
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatsCard>
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getTodayMovements()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Today's Movements
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatsCard>
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getTransferMovements()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Transfers
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatsCard>
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {getUniqueItems()}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Unique Items
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Main Content */}
          <StyledCard>
            <Box
              sx={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: 3,
                borderRadius: "12px 12px 0 0",
              }}
            >
              <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ mb: 2 }}>
                Movement History
              </Typography>

              {/* Search and Filters */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search by item, user, or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant={filter === "all" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => setFilter("all")}
                      sx={{
                        bgcolor: filter === "all" ? "#1e293b" : "transparent",
                        color: filter === "all" ? "white" : "#64748b",
                        borderColor: "#d1d5db",
                        "&:hover": { bgcolor: filter === "all" ? "#334155" : "#f8fafc" },
                      }}
                    >
                      All ({movements?.length})
                    </Button>
                    <Button
                      variant={filter === "in" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => setFilter("in")}
                      sx={{
                        bgcolor: filter === "in" ? "#10b981" : "transparent",
                        color: filter === "in" ? "white" : "#10b981",
                        borderColor: "#10b981",
                        "&:hover": { bgcolor: filter === "in" ? "#059669" : "#f0fdf4" },
                      }}
                    >
                      Inbound ({getInboundMovements()})
                    </Button>
                    <Button
                      variant={filter === "out" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => setFilter("out")}
                      sx={{
                        bgcolor: filter === "out" ? "#ef4444" : "transparent",
                        color: filter === "out" ? "white" : "#ef4444",
                        borderColor: "#ef4444",
                        "&:hover": { bgcolor: filter === "out" ? "#dc2626" : "#fef2f2" },
                      }}
                    >
                      Outbound ({getOutboundMovements()})
                    </Button>
                    <Button
                      variant={filter === "transfer" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => setFilter("transfer")}
                      sx={{
                        bgcolor: filter === "transfer" ? "#f59e0b" : "transparent",
                        color: filter === "transfer" ? "white" : "#f59e0b",
                        borderColor: "#f59e0b",
                        "&:hover": { bgcolor: filter === "transfer" ? "#d97706" : "#fefbf2" },
                      }}
                    >
                      Transfers ({getTransferMovements()})
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="#64748b" sx={{ mt: 2 }}>
                    Loading stock movements...
                  </Typography>
                </Box>
              ) : filteredMovements?.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <TimelineIcon sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
                  <Typography variant="h6" color="#64748b" sx={{ mb: 1 }}>
                    {searchTerm || filter !== "all" ? "No matching movements found" : "No stock movements recorded"}
                  </Typography>
                  <Typography variant="body2" color="#94a3b8">
                    {searchTerm || filter !== "all"
                      ? "Try adjusting your search or filter criteria"
                      : "Stock movements will appear here once transactions are recorded"}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 3 }}>
                  <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" color="#64748b">
                      Showing {filteredMovements.length} of {movements?.length} movements
                    </Typography>
                    {(searchTerm || filter !== "all") && (
                      <Chip
                        label={`Filtered: ${filter !== "all" ? filter : "search"}`}
                        size="small"
                        onDelete={() => {
                          setSearchTerm("")
                          setFilter("all")
                        }}
                        sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
                      />
                    )}
                  </Box>
                
                  <StockMovementList movements={filteredMovements} />
                </Box>
              )}
            </CardContent>
          </StyledCard>
        </Container>
      </IonContent>
    </IonPage>
  )
}

export default Movements
